import React, { useMemo } from 'react';

import {
	type PostOfficeAllMessages,
	useAllMessages,
} from '@atlassian/post-office-headless-api/hooks/useAllMessages';
import type {
	PlacementMessage,
	PlacementMessageDiscriminant,
} from '@atlassian/post-office-headless-api/hooks/usePlacementData';

import { type ThirdPartyConfigsBootstrap } from '../../../constants/schemas/3p-config';
import {
	mapToAvailableConnectors,
	orderConfigByProductOrder,
	type useOAuthHandlers,
} from '../banner-placement';

type QuickFindHeadlessMessage = PlacementMessageDiscriminant;
type QuickFindHeadlessMessageInstance = PlacementMessage<QuickFindHeadlessMessage>;
type QuickFindHeadlessAvailableConnectors = ReturnType<typeof mapToAvailableConnectors>;

export type QuickFindHeadlessRendererProps = {
	message: QuickFindHeadlessMessageInstance;
	availableConnectors: QuickFindHeadlessAvailableConnectors;
	useOAuthHandlers: useOAuthHandlers;
	dismiss: () => void;
	PostOfficeAllMessages: PostOfficeAllMessages;
	onMessageViewed?: (trackImpression: () => void) => void;
};

export const QuickFindHeadlessMessageRenderer = (
	_props: QuickFindHeadlessRendererProps,
): React.JSX.Element | null => null;

export const createQuickFindHeadlessRendererProps = ({
	message,
	availableConnectors,
	useOAuthHandlers,
	dismissMessage,
	PostOfficeAllMessages,
	onMessageViewed,
}: {
	message: QuickFindHeadlessMessageInstance;
	availableConnectors: QuickFindHeadlessAvailableConnectors;
	useOAuthHandlers: useOAuthHandlers;
	dismissMessage: (messageInstanceId: string) => void;
	PostOfficeAllMessages: PostOfficeAllMessages;
	onMessageViewed?: (trackImpression: () => void) => void;
}): QuickFindHeadlessRendererProps => ({
	message,
	availableConnectors,
	useOAuthHandlers,
	dismiss: () => dismissMessage(message.messageInstanceId),
	PostOfficeAllMessages,
	onMessageViewed,
});

interface QuickFindHeadlessPlacementProps {
	placementId: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProductsOrder: string[]; // Product keys in display order; connectors will be shown in this order
	useOAuthHandlers: useOAuthHandlers;
	onMessageViewed?: (trackImpression: () => void) => void;
}

export const QuickFindHeadlessPlacement = ({
	placementId,
	thirdPartyConfigs,
	availableProductsOrder,
	useOAuthHandlers,
	onMessageViewed,
}: QuickFindHeadlessPlacementProps): React.JSX.Element | null => {
	const { messages, dismissMessage, PostOfficeAllMessages } =
		useAllMessages<QuickFindHeadlessMessage>(placementId);

	const orderedConfigs = useMemo(
		() => orderConfigByProductOrder(thirdPartyConfigs, availableProductsOrder),
		[thirdPartyConfigs, availableProductsOrder],
	);

	const availableConnectors = useMemo(
		() => mapToAvailableConnectors(orderedConfigs),
		[orderedConfigs],
	);

	return (
		<>
			{messages.map((message) => (
				<QuickFindHeadlessMessageRenderer
					key={message.messageInstanceId}
					{...createQuickFindHeadlessRendererProps({
						message,
						availableConnectors,
						useOAuthHandlers,
						dismissMessage,
						PostOfficeAllMessages,
						onMessageViewed,
					})}
				/>
			))}
		</>
	);
};
